import { useMemo } from 'react';
import {
  project as projectSpecifier,
  toString as resourceSpecifierToString,
  view as viewSpecifier,
} from '@gonfalon/resource-specifiers/ast';
import { getAccessCheckQuery } from '@gonfalon/rest-api/queries/authorization';
import { type QueryClient, useQueries } from '@tanstack/react-query';

import type { LinkableResourceType, View } from './types';

/**
 * Role actions gating link/unlink of each resource type to/from a view. The
 * backend checks these on the view's resource identifier (see
 * `internal/views/internal/service/view_flags.go` / `view_segments.go`), so the
 * UI must gate on the same actions (REL-15125).
 */
const linkActionByResourceType = {
  flags: 'linkFlagToView',
  segments: 'linkSegmentToView',
  metrics: 'linkMetricToView',
  'ai-configs': 'linkAIConfigToView',
} as const satisfies Record<LinkableResourceType, string>;

const unlinkActionByResourceType = {
  flags: 'unlinkFlagFromView',
  segments: 'unlinkSegmentFromView',
  metrics: 'unlinkMetricFromView',
  'ai-configs': 'unlinkAIConfigFromView',
} as const satisfies Record<LinkableResourceType, string>;

export function getLinkViewAction(resourceType: LinkableResourceType) {
  return linkActionByResourceType[resourceType];
}

export function getUnlinkViewAction(resourceType: LinkableResourceType) {
  return unlinkActionByResourceType[resourceType];
}

/**
 * Standard denial copy for link/unlink controls, matching the app-wide
 * permission-denied phrasing.
 */
export const LINK_ACCESS_DENIED_MESSAGE = "Your permissions don't allow you to perform this action";

/**
 * Copy for a check that errored (matches `AccessCheck`'s fallback). Controls
 * still lock (fail closed), but the reason must not claim a permission denial
 * the member may not have.
 */
export const LINK_ACCESS_CHECK_FAILED_MESSAGE = 'Could not verify permissions. Please refresh and try again.';

/** Matches `useAccessCheck` — access checks change rarely. */
const ACCESS_CHECK_STALE_TIME = 5 * 60 * 1000;

/** The subset of a project the view resource specifier needs. */
type ProjectInput = { key: string; tags?: string[] | null };

/** The subset of a view the resource specifier needs. */
type ViewInput = Pick<View, 'key' | 'tags' | '_affectsSdkPayload'>;

/**
 * Build the view resource specifier string for a link/unlink access check.
 *
 * Carries the view's `_affectsSdkPayload` property as the
 * `{affectsSdkPayload:<bool>}` selector — required for the access-check
 * endpoint to match role statements scoped by that property (the
 * payload-filtering custom-role shape from REL-15125). Without it, such
 * statements never match and the answer is wrong for exactly those roles.
 */
function viewLinkAccessResource(project: ProjectInput, view: ViewInput): string {
  return resourceSpecifierToString(
    viewSpecifier(
      projectSpecifier(project.key, project.tags ?? []),
      view.key,
      view.tags ?? [],
      undefined,
      undefined,
      view._affectsSdkPayload,
    ),
  );
}

function isAllowedResponse(data: unknown): boolean {
  return !!data && typeof data === 'object' && 'allowed' in data && data.allowed === true;
}

function lockedReasonFor(state: { isAllowed: boolean; isLoading: boolean; isError: boolean }): string | undefined {
  if (state.isAllowed || state.isLoading) {
    return undefined;
  }
  return state.isError ? LINK_ACCESS_CHECK_FAILED_MESSAGE : LINK_ACCESS_DENIED_MESSAGE;
}

export type ViewLinkAccess = {
  isAllowed: boolean;
  isLoading: boolean;
  /**
   * Why the control is locked, once the check has resolved: the permission
   * denial copy for a real denial, or the could-not-verify copy for an
   * errored check. Undefined while allowed or still loading.
   */
  lockedReason?: string;
};

/**
 * Stable identities for the gating-off path. Collection components pass the
 * returned accessor as a `dependencies` entry to invalidate cached rows, so
 * while gating is off the accessor (and everything feeding it) must keep a
 * constant identity — otherwise every parent render (e.g. filter typing)
 * would needlessly bust the row cache.
 */
const UNGATED_VIEWS: View[] = [];
const UNGATED_ACCESS: ViewLinkAccess = { isAllowed: true, isLoading: false };
const allowAllViewLinkAccess = (_viewKey: string): ViewLinkAccess => UNGATED_ACCESS;

/**
 * Per-view link/unlink access for a list of views, via the access-check
 * endpoint (batched by `getAccessCheckQuery`'s bulk task runner and cached for
 * `ACCESS_CHECK_STALE_TIME`).
 *
 * Each view is checked for the direction its selection toggle would fire:
 * views in `selectedViewKeys` check the unlink action, the rest check the link
 * action. Results fail closed — `isAllowed` is false until the check resolves
 * (and stays false on error).
 *
 * When `isEnabled` is false (or `resourceType` is missing), no checks are
 * issued and every view reads as allowed.
 */
export function useViewLinkAccessByKey({
  isEnabled,
  project,
  resourceType,
  views,
  selectedViewKeys,
}: {
  isEnabled: boolean;
  project: ProjectInput;
  resourceType: LinkableResourceType | undefined;
  views: View[];
  selectedViewKeys: ReadonlySet<string>;
}): (viewKey: string) => ViewLinkAccess {
  const isGating = isEnabled && resourceType !== undefined;
  const checkedViews = isGating ? views : UNGATED_VIEWS;
  const results = useQueries({
    queries:
      isEnabled && resourceType !== undefined
        ? checkedViews.map((view) => ({
            ...getAccessCheckQuery({
              resource: viewLinkAccessResource(project, view),
              action: selectedViewKeys.has(view.key)
                ? unlinkActionByResourceType[resourceType]
                : linkActionByResourceType[resourceType],
            }),
            staleTime: ACCESS_CHECK_STALE_TIME,
          }))
        : [],
  });

  // The dialogs feed this accessor to collection `dependencies`, which
  // invalidates React Aria's cached rows whenever the accessor's identity
  // changes — so it must only change when the access data does. `useQueries`
  // returns a fresh array each render, so the memo is keyed on a content
  // signature instead of array identity.
  const accessStates = checkedViews.map((view, index) => {
    const result = results[index];
    return {
      key: view.key,
      isAllowed: !result.isLoading && !result.error && isAllowedResponse(result.data),
      isLoading: result.isLoading,
      isError: !result.isLoading && !!result.error,
    };
  });
  const accessSignature = JSON.stringify(accessStates);
  const getGatedAccess = useMemo(() => {
    const stableAccessStates: Array<{
      key: string;
      isAllowed: boolean;
      isLoading: boolean;
      isError: boolean;
    }> = JSON.parse(accessSignature);
    const accessByKey = new Map<string, ViewLinkAccess>(
      stableAccessStates.map((state) => [
        state.key,
        {
          isAllowed: state.isAllowed,
          isLoading: state.isLoading,
          lockedReason: lockedReasonFor(state),
        },
      ]),
    );
    return (viewKey: string) => accessByKey.get(viewKey) ?? UNGATED_ACCESS;
  }, [accessSignature]);

  return isGating ? getGatedAccess : allowAllViewLinkAccess;
}

/**
 * One-off link access check for a freshly created view (the picker's
 * create-view path commits a real link via `setOnViewCreated`, outside any
 * hook). Fails closed on request errors, but reports them distinctly
 * (`failedToCheck`) so callers don't present an outage as a permission denial.
 */
export async function fetchCanLinkResourceToView(
  queryClient: QueryClient,
  { project, resourceType, view }: { project: ProjectInput; resourceType: LinkableResourceType; view: ViewInput },
): Promise<{ isAllowed: boolean; failedToCheck: boolean }> {
  try {
    const data = await queryClient.fetchQuery({
      ...getAccessCheckQuery({
        resource: viewLinkAccessResource(project, view),
        action: linkActionByResourceType[resourceType],
      }),
      staleTime: ACCESS_CHECK_STALE_TIME,
    });
    return { isAllowed: isAllowedResponse(data), failedToCheck: false };
  } catch {
    return { isAllowed: false, failedToCheck: true };
  }
}
